"use client";

import { motion } from "framer-motion";
import { MonitorSmartphone, Building2, ShieldCheck } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type Objection = {
  icon: React.ElementType;
  question: string;
  answer: string;
  accent: "primary" | "emerald" | "sky";
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const OBJECTIONS: Objection[] = [
  {
    icon: MonitorSmartphone,
    question: "Minha equipe não é técnica",
    answer:
      "Interface simples, pensada para o dia a dia da loja. Treinamento e onboarding inclusos no plano — sua equipe estará operando em menos de 48h.",
    accent: "emerald",
  },
  {
    icon: Building2,
    question: "Tenho mais de uma loja",
    answer:
      "O NoWey foi construído para multi-loja. Cada unidade tem seu acesso independente, e o gestor enxerga tudo consolidado em um único painel.",
    accent: "primary",
  },
  {
    icon: ShieldCheck,
    question: "E a segurança dos meus dados?",
    answer:
      "Seus dados ficam no seu ambiente, com backups automáticos diários, criptografia e controle de acesso granular por perfil de usuário.",
    accent: "sky",
  },
] as const;

// ---------------------------------------------------------------------------
// Static accent maps
// ---------------------------------------------------------------------------
const ACCENT_ICON_WRAP: Record<Objection["accent"], string> = {
  primary: "bg-primary/15 border-primary/30 text-primary",
  emerald: "bg-emerald-500/10 border-emerald-500/25 text-emerald-400",
  sky:     "bg-sky-500/10 border-sky-500/25 text-sky-400",
};

const ACCENT_QUESTION: Record<Objection["accent"], string> = {
  primary: "text-primary",
  emerald: "text-emerald-400",
  sky:     "text-sky-400",
};

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13 } },
} as const;

const cardVariants = {
  hidden:   { opacity: 0, y: 28 },
  visible:  { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
} as const;

// ---------------------------------------------------------------------------
// ObjectionsSection
// ---------------------------------------------------------------------------
export default function ObjectionsSection() {
  return (
    <section
      id="por-que-nowey"
      className="relative z-10 py-24 bg-card/20 border-y border-border/40 overflow-hidden"
      aria-labelledby="objections-headline"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[80%] bg-primary/4 rounded-full blur-[140px]" />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14"
        >
          <h2
            id="objections-headline"
            className="text-3xl md:text-5xl font-black tracking-tight text-foreground mb-4"
          >
            Feito para a realidade{" "}
            <span className="text-primary">da sua rede</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-md mx-auto">
            Respondemos as dúvidas mais comuns antes mesmo de você perguntar.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
          role="list"
          aria-label="Objeções frequentes e respostas"
        >
          {OBJECTIONS.map((obj) => {
            const Icon = obj.icon;
            return (
              <motion.article
                key={obj.question}
                variants={cardVariants}
                role="listitem"
                className="group flex flex-col gap-5 rounded-2xl border border-border/50 bg-card/60 p-7 backdrop-blur-sm transition-colors duration-300 hover:border-border"
                aria-label={obj.question}
              >
                {/* Icon */}
                <div
                  className={`w-12 h-12 rounded-xl border flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 ${ACCENT_ICON_WRAP[obj.accent]}`}
                >
                  <Icon className="w-5 h-5" aria-hidden />
                </div>

                {/* Question */}
                <h3
                  className={`text-lg font-bold leading-snug ${ACCENT_QUESTION[obj.accent]}`}
                >
                  {obj.question}
                </h3>

                {/* Answer */}
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {obj.answer}
                </p>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
